import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService, HttpMethod } from '../../../shared/services/api/api.service';
import { ToasterService } from '../../../shared/services/toaster/toaster.service';
import { User } from '../../../shared/services/users/users.service';
import { AiBudgetService } from '../../../shared/services/ai-budget/ai-budget.service';
import { AiBudgetSnapshot } from '../../../shared/services/ai-budget/ai-budget.types';
import { UserRoleService } from '../../../shared/services/user-role/user-role.service';

/** Row from GET /organizations */
export interface OrganizationRow {
  ORGANIZATION_ID?: number;
  ORGANIZATION_NAME: string;
  ORGANIZATION_SLUG?: string;
  CREATED_AT?: string | Date;
}

/** Member row nested in organization-groups response */
interface GroupMemberRow {
  ORGANIZATION_ID?: number;
  organization?: {
    ORGANIZATION_ID: number;
    ORGANIZATION_NAME: string;
  };
}

/** Row from GET /organization-groups */
export interface OrgGroupRow {
  GROUP_ID: number;
  GROUP_NAME: string;
  DESCRIPTION?: string | null;
  members?: GroupMemberRow[];
  supervisors?: Array<{
    USER_ID: number;
    FIRST_NAME?: string;
    LAST_NAME?: string;
    EMAIL?: string;
  }>;
}

/** Row from GET /audit-trail */
export interface AuditEntryRow {
  AUDIT_ID?: number;
  USER_ID: number;
  ACTION_TYPE: string;
  RESOURCE_TYPE: string;
  RESOURCE_ID: string;
  DETAILS?: Record<string, unknown> | null;
  TIMESTAMP?: string | Date;
}

export type AdminTab = 'organizations' | 'groups' | 'audit' | 'budgets';

interface OrgBudgetRow {
  orgId: number;
  orgName: string;
  snapshot: AiBudgetSnapshot | null;
}

@Component({
  selector: 'app-organizations',
  templateUrl: './organizations.component.html',
  styleUrls: ['./organizations.component.scss'],
})
export class OrganizationsComponent implements OnInit, OnDestroy {
  activeTab: AdminTab = 'organizations';

  organizations: OrganizationRow[] = [];
  orgGroups: OrgGroupRow[] = [];
  auditEntries: AuditEntryRow[] = [];
  supervisorUsers: User[] = [];

  orgBudgets: OrgBudgetRow[] = [];
  selectedBudgetOrgId: number | null = null;
  editModalOpen = false;

  private readonly destroy$ = new Subject<void>();

  showCreateOrgForm = false;
  showCreateGroupForm = false;

  newOrgName = '';
  newGroupName = '';
  newGroupDescription = '';

  editingOrgId: number | null = null;
  editOrgName = '';

  /** Per-group: org id to add (string for select binding) */
  selectedOrgIdToAdd: Record<number, string> = {};
  /** Per-group: supervisor user id (string for select binding) */
  selectedSupervisorUserId: Record<number, string> = {};

  auditDateFrom = '';
  auditDateTo = '';

  isLoadingOrganizations = false;
  isLoadingGroups = false;
  isLoadingAudit = false;
  isLoadingSupervisors = false;

  constructor(
    private apiService: ApiService,
    private toaster: ToasterService,
    private aiBudgetService: AiBudgetService,
    public userRoleService: UserRoleService,
  ) {}

  ngOnInit(): void {
    void this.bootstrap();
  }

  private async bootstrap(): Promise<void> {
    await Promise.all([
      this.loadOrganizations(),
      this.loadOrgGroups(),
      this.loadSupervisorCandidates(),
    ]);
  }

  setTab(tab: AdminTab): void {
    this.activeTab = tab;
    if (tab === 'audit' && this.auditEntries.length === 0 && !this.isLoadingAudit) {
      void this.loadAuditTrail();
    }
    if (tab === 'budgets') {
      this.loadOrgBudgets();
    }
  }

  async loadOrganizations(): Promise<void> {
    this.isLoadingOrganizations = true;
    try {
      const data = await this.apiService.call<OrganizationRow[]>(
        HttpMethod.GET,
        'organizations?page=1&pageSize=500',
      );
      this.organizations = Array.isArray(data) ? data : [];
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Failed to load organizations: ${msg}`, 5000);
      console.error(error);
    } finally {
      this.isLoadingOrganizations = false;
    }
  }

  async createOrg(name: string): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) {
      this.toaster.warning('Organization name is required.', 4000);
      return;
    }
    try {
      await this.apiService.call(HttpMethod.POST, 'organizations', {
        ORGANIZATION_NAME: trimmed,
      });
      this.toaster.success('Organization created.', 4000);
      this.newOrgName = '';
      this.showCreateOrgForm = false;
      await this.loadOrganizations();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Create organization failed: ${msg}`, 5000);
      console.error(error);
    }
  }

  startEditOrg(org: OrganizationRow): void {
    const id = org.ORGANIZATION_ID;
    if (id == null) {
      return;
    }
    this.editingOrgId = id;
    this.editOrgName = org.ORGANIZATION_NAME;
  }

  cancelEditOrg(): void {
    this.editingOrgId = null;
    this.editOrgName = '';
  }

  async saveEditOrg(): Promise<void> {
    if (this.editingOrgId == null) {
      return;
    }
    const ok = await this.updateOrg(this.editingOrgId, this.editOrgName);
    if (ok) {
      this.cancelEditOrg();
    }
  }

  async updateOrg(id: number, name: string): Promise<boolean> {
    const trimmed = name.trim();
    if (!trimmed) {
      this.toaster.warning('Organization name is required.', 4000);
      return false;
    }
    try {
      await this.apiService.call(HttpMethod.PUT, `organizations/${id}`, {
        ORGANIZATION_NAME: trimmed,
      });
      this.toaster.success('Organization updated.', 4000);
      await this.loadOrganizations();
      return true;
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Update organization failed: ${msg}`, 5000);
      console.error(error);
      return false;
    }
  }

  async deleteOrg(id: number): Promise<void> {
    if (!window.confirm(`Delete organization ${id}? This may fail if data exists.`)) {
      return;
    }
    try {
      await this.apiService.call(HttpMethod.DELETE, `organizations/${id}`);
      this.toaster.success('Organization deleted.', 4000);
      await this.loadOrganizations();
      await this.loadOrgGroups();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Delete organization failed: ${msg}`, 5000);
      console.error(error);
    }
  }

  async loadOrgGroups(): Promise<void> {
    this.isLoadingGroups = true;
    try {
      const data = await this.apiService.call<OrgGroupRow[]>(
        HttpMethod.GET,
        'organization-groups',
      );
      this.orgGroups = Array.isArray(data) ? data : [];
      this.selectedOrgIdToAdd = {};
      this.selectedSupervisorUserId = {};
      for (const g of this.orgGroups) {
        this.selectedOrgIdToAdd[g.GROUP_ID] = '';
        this.selectedSupervisorUserId[g.GROUP_ID] = '';
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Failed to load organization groups: ${msg}`, 5000);
      console.error(error);
    } finally {
      this.isLoadingGroups = false;
    }
  }

  async createGroup(name: string, description: string): Promise<void> {
    const n = name.trim();
    if (!n) {
      this.toaster.warning('Group name is required.', 4000);
      return;
    }
    try {
      await this.apiService.call(HttpMethod.POST, 'organization-groups', {
        name: n,
        description: description.trim() || undefined,
      });
      this.toaster.success('Group created.', 4000);
      this.newGroupName = '';
      this.newGroupDescription = '';
      this.showCreateGroupForm = false;
      await this.loadOrgGroups();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Create group failed: ${msg}`, 5000);
      console.error(error);
    }
  }

  async deleteGroup(id: number): Promise<void> {
    if (!window.confirm(`Delete organization group ${id}?`)) {
      return;
    }
    try {
      await this.apiService.call(HttpMethod.DELETE, `organization-groups/${id}`);
      this.toaster.success('Group deleted.', 4000);
      await this.loadOrgGroups();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Delete group failed: ${msg}`, 5000);
      console.error(error);
    }
  }

  async addOrgToGroup(groupId: number, organizationId: number): Promise<void> {
    try {
      await this.apiService.call(
        HttpMethod.POST,
        `organization-groups/${groupId}/orgs`,
        { organizationId },
      );
      this.toaster.success('Organization added to group.', 4000);
      this.selectedOrgIdToAdd[groupId] = '';
      await this.loadOrgGroups();
      await this.loadOrganizations();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Add org to group failed: ${msg}`, 5000);
      console.error(error);
    }
  }

  onAddOrgToGroupClick(group: OrgGroupRow): void {
    const gid = group.GROUP_ID;
    const raw = this.selectedOrgIdToAdd[gid];
    const organizationId = raw ? parseInt(raw, 10) : NaN;
    if (!Number.isFinite(organizationId)) {
      this.toaster.warning('Select an organization to add.', 4000);
      return;
    }
    void this.addOrgToGroup(gid, organizationId);
  }

  async removeOrgFromGroup(groupId: number, organizationId: number): Promise<void> {
    try {
      await this.apiService.call(
        HttpMethod.DELETE,
        `organization-groups/${groupId}/orgs/${organizationId}`,
      );
      this.toaster.success('Organization removed from group.', 4000);
      await this.loadOrgGroups();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Remove org from group failed: ${msg}`, 5000);
      console.error(error);
    }
  }

  async assignSupervisorToGroup(groupId: number, userId: number): Promise<void> {
    try {
      await this.apiService.call(
        HttpMethod.POST,
        `organization-groups/${groupId}/supervisors`,
        { userId },
      );
      this.toaster.success('Supervisor assigned to group.', 4000);
      this.selectedSupervisorUserId[groupId] = '';
      await this.loadOrgGroups();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Assign supervisor failed: ${msg}`, 5000);
      console.error(error);
    }
  }

  onAssignSupervisorClick(group: OrgGroupRow): void {
    const gid = group.GROUP_ID;
    const raw = this.selectedSupervisorUserId[gid];
    const userId = raw ? parseInt(raw, 10) : NaN;
    if (!Number.isFinite(userId)) {
      this.toaster.warning('Select a supervisor user.', 4000);
      return;
    }
    void this.assignSupervisorToGroup(gid, userId);
  }

  private async loadSupervisorCandidates(): Promise<void> {
    this.isLoadingSupervisors = true;
    try {
      const res = await this.apiService.call<{
        users: User[];
      }>(HttpMethod.GET, 'users?role=SUPERVISOR&pageSize=500&page=1');
      this.supervisorUsers = res?.users ?? [];
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Failed to load supervisor users: ${msg}`, 5000);
      console.error(error);
    } finally {
      this.isLoadingSupervisors = false;
    }
  }

  orgsAvailableForGroup(group: OrgGroupRow): OrganizationRow[] {
    const memberIds = new Set<number>();
    for (const m of group.members ?? []) {
      const id =
        m.organization?.ORGANIZATION_ID ?? m.ORGANIZATION_ID;
      if (typeof id === 'number') {
        memberIds.add(id);
      }
    }
    return this.organizations.filter(
      (o) =>
        o.ORGANIZATION_ID != null && !memberIds.has(o.ORGANIZATION_ID),
    );
  }

  orgGroupOrgCount(group: OrgGroupRow): number {
    return group.members?.length ?? 0;
  }

  supervisorSummary(group: OrgGroupRow): string {
    const list = group.supervisors ?? [];
    if (list.length === 0) {
      return '—';
    }
    return list
      .map(
        (s) =>
          `${s.FIRST_NAME ?? ''} ${s.LAST_NAME ?? ''} (${s.USER_ID})`.trim(),
      )
      .join('; ');
  }

  async loadAuditTrail(filters?: { dateFrom?: string; dateTo?: string }): Promise<void> {
    this.isLoadingAudit = true;
    const from = filters?.dateFrom ?? this.auditDateFrom;
    const to = filters?.dateTo ?? this.auditDateTo;
    try {
      const params = new URLSearchParams();
      if (from) {
        params.set('dateFrom', from);
      }
      if (to) {
        params.set('dateTo', to);
      }
      const qs = params.toString();
      const path = qs ? `audit-trail?${qs}` : 'audit-trail';
      const data = await this.apiService.call<AuditEntryRow[]>(
        HttpMethod.GET,
        path,
      );
      this.auditEntries = Array.isArray(data) ? data : [];
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Failed to load audit trail: ${msg}`, 5000);
      console.error(error);
    } finally {
      this.isLoadingAudit = false;
    }
  }

  applyAuditFilters(): void {
    void this.loadAuditTrail({
      dateFrom: this.auditDateFrom || undefined,
      dateTo: this.auditDateTo || undefined,
    });
  }

  formatDetails(details: Record<string, unknown> | null | undefined): string {
    if (details == null) {
      return '—';
    }
    try {
      return JSON.stringify(details);
    } catch {
      return String(details);
    }
  }

  formatWhen(ts: string | Date | undefined): string {
    if (ts == null) {
      return '—';
    }
    const d = typeof ts === 'string' ? new Date(ts) : ts;
    if (Number.isNaN(d.getTime())) {
      return String(ts);
    }
    return d.toLocaleString();
  }

  loadOrgBudgets(): void {
    this.orgBudgets = this.organizations
      .filter(o => o.ORGANIZATION_ID != null)
      .map(o => ({
        orgId: o.ORGANIZATION_ID as number,
        orgName: o.ORGANIZATION_NAME,
        snapshot: null,
      }));
    for (const row of this.orgBudgets) {
      this.aiBudgetService.getOrgBudget(row.orgId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: snap => { row.snapshot = snap; },
          error: () => { /* leave snapshot null */ },
        });
    }
  }

  onEditBudgetClick(orgId: number): void {
    this.selectedBudgetOrgId = orgId;
    this.editModalOpen = true;
  }

  onBudgetModalClosed(): void {
    this.editModalOpen = false;
    this.selectedBudgetOrgId = null;
  }

  onBudgetSaved(snapshot: AiBudgetSnapshot): void {
    const row = this.orgBudgets.find(r => r.orgId === this.selectedBudgetOrgId);
    if (row) {
      row.snapshot = snapshot;
    }
  }

  get selectedBudgetSnapshot(): AiBudgetSnapshot | null {
    if (this.selectedBudgetOrgId == null) {
      return null;
    }
    return this.orgBudgets.find(r => r.orgId === this.selectedBudgetOrgId)?.snapshot ?? null;
  }

  trackByOrgId(_: number, row: OrgBudgetRow): number {
    return row.orgId;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
